import {
	AttributeValue,
	LogicalOperator,
	RelationActions,
	RelationCondition,
	Relation,
	RelationTypeEnum,
} from '@/api/AttributeValueApi';
import { AttributeType } from '@/api/AttributeApi';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { ProductModule, ProductModuleType } from '@/api/ProductModuleApi';
import { SlotType } from '@/renderer/objects/ConnectingSlot';
import { SerializedRenderer } from '@/renderer/types';
import {
	SelectedSlot,
	CreatorOptions,
	AttributeWithValues,
	CreatorState,
	SavedCreatorState,
	SerializedCreatorState,
} from '@/types/creator';
import { UUID } from '@/types/types';
import { groupBy } from 'lodash';
import APP_CONFIG from '@/configs/appConfig';

export function checkProductModuleMatching(
	productModule: ProductModule,
	selectedSlot: SelectedSlot,
	slotsProductModule: ProductModule
) {
	const productModuleType = productModule.moduleType;

	if (slotsProductModule.modulesBlacklistIds.includes(productModule.id))
		return false;
	if (productModule.modulesBlacklistIds.includes(slotsProductModule.id))
		return false;

	switch (selectedSlot.slotType) {
		case SlotType.LEFT:
			return [ProductModuleType.LEFT, ProductModuleType.LINE].includes(
				productModuleType
			);
		case SlotType.RIGHT:
			return [ProductModuleType.RIGHT, ProductModuleType.LINE].includes(
				productModuleType
			);
		case SlotType.UNIVERSAL:
			return true;
		case SlotType.HOST:
			return productModule.modulesWhitelistIds.includes(slotsProductModule.id);

		default:
			return false;
	}
}

export function checkProductModuleSwapMatching(
	productModule: ProductModule,
	swapDetails: CreatorOptions['swapDetails'],
	creatorProductModules: CreatorProductModule[]
) {
	const { slotTypes, modulesIds, creatorProductModule } = swapDetails!;

	if (creatorProductModule?.productModule!.id === productModule.id)
		return false;

	const connectedModules = creatorProductModules.filter((cpm) =>
		modulesIds.includes(cpm.id!)
	);

	const connectedAccessoryModules = connectedModules.filter(
		(connectedModule) =>
			connectedModule.productModule!.moduleType === ProductModuleType.ACCESSORY
	);
	const connectedNonAccessoryModules = connectedModules.filter(
		(connectedModule) =>
			connectedModule.productModule!.moduleType !== ProductModuleType.ACCESSORY
	);

	if (
		connectedAccessoryModules.some(
			(connectedAccessoryModule) =>
				!connectedAccessoryModule.productModule!.modulesWhitelistIds.includes(
					productModule.id
				)
		)
	)
		return false;

	const modulesToValidate = [
		...connectedNonAccessoryModules,
		...connectedAccessoryModules,
	];

	for (const connectedModule of modulesToValidate) {
		if (
			connectedModule.productModule!.modulesBlacklistIds.includes(
				productModule.id
			)
		)
			return false;
		if (
			productModule.modulesBlacklistIds.includes(
				connectedModule.productModule!.id
			)
		)
			return false;
	}

	const productModuleType = productModule.moduleType;
	const forbiddenModuleTypes = [];

	if (slotTypes.includes(SlotType.LEFT) && slotTypes.includes(SlotType.RIGHT))
		forbiddenModuleTypes.push(ProductModuleType.LEFT, ProductModuleType.RIGHT);
	if (slotTypes.includes(SlotType.LEFT))
		forbiddenModuleTypes.push(ProductModuleType.LEFT);
	if (slotTypes.includes(SlotType.RIGHT))
		forbiddenModuleTypes.push(ProductModuleType.RIGHT);

	return !forbiddenModuleTypes.includes(productModuleType);
}

export function groupAttributeValues(attributeValues: AttributeValue[]) {
	const grouped = groupBy(attributeValues, 'attribute.id');

	const result = [];

	for (const values of Object.values(grouped)) {
		result.push({
			...values[0].attribute,
			values,
		});
	}

	return result;
}

function conditionMatches(
	condition: RelationCondition,
	selectedIds: Set<UUID>
) {
	if (condition.type !== RelationTypeEnum.ATTRIBUTE_VALUE) return false;
	if (condition.objectId == null) return false;
	return selectedIds.has(condition.objectId);
}

function evaluateConditions(
	conditions: RelationCondition[],
	operator: LogicalOperator,
	selectedIds: Set<UUID>
) {
	if (conditions.length === 0) return true;
	if (operator === LogicalOperator.AND)
		return conditions.every((c) => conditionMatches(c, selectedIds));
	return conditions.some((c) => conditionMatches(c, selectedIds));
}

export function evaluateRelationVisibility(
	relation: Relation,
	selectedAttributeValues: AttributeValue[]
) {
	const selectedIds = new Set(selectedAttributeValues.map((v) => v.id));
	const compound = evaluateConditions(
		relation.conditions,
		relation.operator,
		selectedIds
	);
	if (relation.action === RelationActions.SHOW) return compound;
	if (relation.action === RelationActions.HIDE) return !compound;
	return true;
}

export function isAttributeValueVisibleByRelation(
	value: AttributeValue,
	selectedAttributeValues: AttributeValue[]
) {
	if (!value.relation) return true;
	return evaluateRelationVisibility(value.relation, selectedAttributeValues);
}

function isSelectedValueStillValid(
	value: AttributeValue,
	attributesWithValues: AttributeWithValues[],
	selectedList: AttributeValue[]
) {
	const attr = attributesWithValues.find((a) => a.id === value.attribute.id);
	if (!attr) return false;
	const option = attr.values.find((v) => v.id === value.id);
	if (!option) return false;
	return isAttributeValueVisibleByRelation(option, selectedList);
}

function isSelectedValueValidForCreatorModule(
	value: AttributeValue,
	attributesWithValues: AttributeWithValues[],
	selectedList: AttributeValue[],
	productModule: ProductModule
) {
	if (
		productModule.hasValuesOverride &&
		value.attribute.type === AttributeType.MODULE &&
		!productModule.attributeValueOverridesIds.includes(value.id)
	)
		return false;
	return isSelectedValueStillValid(value, attributesWithValues, selectedList);
}

function pruneGlobalSelections(
	attributesWithValues: AttributeWithValues[],
	globalMap: CreatorState['globalSelectedAttributeValues']
) {
	let changed = true;
	while (changed) {
		changed = false;
		const selectedList = Object.values(globalMap).filter(
			(v): v is AttributeValue => v != null
		);
		for (const key of Object.keys(globalMap) as UUID[]) {
			const val = globalMap[key];
			if (!val) continue;
			if (!isSelectedValueStillValid(val, attributesWithValues, selectedList)) {
				globalMap[key] = null;
				changed = true;
			}
		}
	}
}

function pruneModuleSelections(
	attributesWithValues: AttributeWithValues[],
	mod: CreatorProductModule
) {
	let changed = true;
	while (changed) {
		changed = false;
		const selectedList = mod.selectedAttributeValues;
		const next = selectedList!.filter((v) =>
			isSelectedValueValidForCreatorModule(
				v,
				attributesWithValues,
				selectedList!,
				mod.productModule!
			)
		);
		if (next.length !== selectedList!.length) {
			mod.selectedAttributeValues = next;
			changed = true;
		}
	}
}

export function pruneCreatorAttributeSelections(state: CreatorState) {
	const { attributesWithValues, globalSelectedAttributeValues } = state;
	const modules = state.options.isPresetMode
		? state.preset.creatorProductModules
		: state.project.creatorProductModules;

	pruneGlobalSelections(attributesWithValues, globalSelectedAttributeValues);
	for (const mod of modules) {
		pruneModuleSelections(attributesWithValues, mod);
	}

	const selected = state.options.selectedCreatorProductModule;
	if (selected) {
		const mod = modules.find((m) => m.id === selected.id);
		if (mod) selected.selectedAttributeValues = mod.selectedAttributeValues;
	}
}

export function mapAttributesByRelation(
	attributesWithValues: AttributeWithValues[],
	selectedAttributeValues: AttributeValue[]
) {
	return attributesWithValues
		.map((attribute) => {
			const values = attribute.values
				.filter((value) =>
					isAttributeValueVisibleByRelation(value, selectedAttributeValues)
				)
				.sort((a, b) => a.listOrder - b.listOrder);

			return { ...attribute, values: structuredClone(values) };
		})
		.filter((attr) => !!attr.values.length)
		.sort((a, b) => a.listOrder - b.listOrder);
}

export function generateProjectPayload(
	{
		options,
		project,
		selectedProductModuleCollection,
		selectedPreset,
		attributesWithValues,
		globalSelectedAttributeValues,
	}: CreatorState,
	serializedRenderer: SerializedRenderer
): SavedCreatorState {
	return {
		project,
		selectedProductModuleCollection,
		selectedPreset,
		attributesWithValues,
		globalSelectedAttributeValues,
		options,
		serializedRenderer,
	};
}

export function serializeCreatorState(
	{
		project,
		options,
		selectedProductModuleCollection,
		selectedPreset,
		globalSelectedAttributeValues,
	}: CreatorState,
	serializedRenderer: SerializedRenderer
): SerializedCreatorState {
	const globalSelectedAttributeValuesIds: SerializedCreatorState['globalSelectedAttributeValuesIds'] =
		{};

	let attributeId: UUID;
	for (attributeId in globalSelectedAttributeValues) {
		const attributeValue = globalSelectedAttributeValues[attributeId];

		globalSelectedAttributeValuesIds[attributeId] = attributeValue?.id || null;
	}

	return {
		projectId: project.id,
		selectedProductModuleCollectionId: selectedProductModuleCollection!.id,
		selectedPreset,
		globalSelectedAttributeValuesIds,
		options,
		serializedRenderer,
	};
}

export function saveProjectToLocal(
	state: CreatorState,
	rendererState: SerializedRenderer
) {
	if (import.meta.env.DEV) return;

	localStorage.setItem(
		APP_CONFIG.TEMP_PROJECT_STORAGE_KEY,
		JSON.stringify(generateProjectPayload(state, rendererState))
	);
}

export function buildConfigurationUrl(storeId: UUID, projectId: UUID) {
	const url = new URL(import.meta.env.VITE_CONFIGURATOR_URL);
	url.searchParams.set('storeId', storeId);
	url.searchParams.set('project', projectId);
	return url.toString();
}
